import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './config';

const LEADERBOARD_COLLECTION = 'leaderboards';
const LEADERBOARD_TTL_MS = 10 * 60 * 1000; // 10 min
const leaderboardCache = new Map();

function isOfflineError(err) {
  return err?.code === 'unavailable' || err?.message?.includes('offline');
}

/** Update a user's leaderboard entry for a category */
export async function updateLeaderboardEntry(category, uid, displayName, value) {
  try {
    const entryRef = doc(db, LEADERBOARD_COLLECTION, category, 'entries', uid);
    const snap = await getDoc(entryRef);
    const prev = snap.exists() ? snap.data() : undefined;
    const currentRaw = prev != null ? (prev.score ?? prev.value ?? 0) : 0;
    const current = (currentRaw ?? 0) | 0;
    if (value > current) {
      await setDoc(entryRef, {
        displayName: displayName || 'Player',
        score: value | 0,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (err) {
    if (!isOfflineError(err)) throw err;
  }
}

/** Fetch top N entries for a category */
export async function getLeaderboard(category, limitVal = 10) {
  try {
    const cacheKey = `${category}:${limitVal}`;
    const now = Date.now();
    const cached = leaderboardCache.get(cacheKey);
    if (cached && now - cached.at < LEADERBOARD_TTL_MS) {
      return cached.data;
    }

    const entriesRef = collection(db, LEADERBOARD_COLLECTION, category, 'entries');
    const q = query(entriesRef, orderBy('score', 'desc'), limit(limitVal));
    const snapshot = await getDocs(q);

    const data = snapshot.docs.map((docSnap, i) => {
      const d = docSnap.data();
      return {
        rank: i + 1,
        uid: docSnap.id,
        displayName: d?.displayName,
        value: ((d?.score ?? d?.value ?? 0) | 0),
      };
    });
    leaderboardCache.set(cacheKey, { at: now, data });
    return data;
  } catch (err) {
    if (isOfflineError(err)) return [];
    throw err;
  }
}

/** Leaderboard category definitions for UI */
export const LEADERBOARD_CATEGORIES = [
  { id: 'wordmaster_streak', name: 'Word Master Streak', icon: '📝', unit: 'wins' },
  { id: 'global_streak', name: 'Daily Streak', icon: '⚡', unit: 'days' },
  { id: 'crossword_best', name: 'Hidden Words Best', icon: '📐', unit: 'pts' },
  { id: 'full_crossword_best', name: 'Word Square Best', icon: '📰', unit: 'pts' },
  { id: 'letter_hive_best', name: 'Letter Hive Best', iconId: 'letter-hive', unit: 'pts' },
  { id: 'word_rungs_best', name: 'Word Rungs Best', iconId: 'word-rungs', unit: 'pts' },
];
